import { useCallback, useState } from 'react';
import { Methods, Contracts, ContractNames } from '@airdao/bond-periphery';
import { utils, constants, BigNumberish } from 'ethers';

import useBondPeripheryContracts from '../contexts/BondPeripheryContext/useBondPeriphery';
import { InfoDataProps } from './types';
import { useEthersAdapter } from '@airdao/ui-library';

const stakingLockUnLock = async (
  method: 'stakingLock' | 'stakingUnlock',
  contracts: Contracts,
  token: string,
  amount: BigNumberish,
) => {
  if (!Methods[method]) {
    throw new Error(`Unsupported method: ${method}`);
  }

  return await (
    await Methods[method](contracts, ContractNames.TWStaking, token, amount)
  ).wait();
};

const useStakeActionHandler = (
  shouldExecute: boolean,
  method: 'stakingLock' | 'stakingUnlock',
  state: {
    data: InfoDataProps;
    fetchData: () => void;
  },
) => {
  const [value, onUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const contracts = useBondPeripheryContracts();
  const { provider } = useEthersAdapter();
  const { data, fetchData } = state;
  const { stakingTokenAddress } = data;

  const handleDisabled = useCallback(
    (balance: BigNumberish) => {
      return (
        value === '' ||
        Number(value) === 0 ||
        Number(value) > +utils.formatUnits(balance, 18)
      );
    },
    [value],
  );

  const handleMaxInput = useCallback((balance: BigNumberish) => {
    balance && onUserInput(utils.formatUnits(balance, 18));
  }, []);

  const onClickHandler = useCallback(
    async (value: any) => {
      if (!contracts || !provider || !stakingTokenAddress) return;

      try {
        setIsLoading(true);

        if (shouldExecute && stakingTokenAddress !== constants.AddressZero) {
          const tx = await Methods.ERC20Approve(
            stakingTokenAddress,
            contracts.getContractByName(ContractNames.TWStaking).address,
            utils.parseUnits(value, 18).toString(),
            provider?.getSigner(),
          );
          await tx.wait();
        }

        await stakingLockUnLock(
          method,
          contracts,
          stakingTokenAddress,
          utils.parseUnits(value, 18).toString(),
        );
        setIsLoading(false);
      } catch (e: any) {
        console.error('An error occurred while staking tokens:', e);
      } finally {
        setIsLoading(false);
        onUserInput('');
        fetchData();
      }
    },
    [method, shouldExecute, contracts, provider, stakingTokenAddress],
  );

  return {
    userInput: { value, onUserInput },
    isLoading,
    onClickHandler,
    handleDisabled,
    handleMaxInput,
  };
};

export default useStakeActionHandler;
